import toLower from 'lodash/toLower';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import { matchPath, Path, To } from 'react-router-dom';
import {
    ALL_LANGUAGES,
    URL_FOREIGN_LANGUAGE_ROUTE,
    URL_LANGUAGE_LEARNING_ROUTE,
    URL_LOTE_ROUTE
} from '../../shop/language/constants';
import {
    URL_LIBX_ROUTE,
    URL_SUFFIX_LIBX_GENRES,
    URL_SUFFIX_LIBX_LOCAL_REGIONS
} from '../../yourborrowbox/libx/constants';
import { URL_ALLPRODUCTS_ROUTE, URL_FEATURED_ROUTE } from '../../shop/language/lote/constants';
import { CatalogueLocationDescriptorParams } from '../utils';
import { AgeCategory } from '../../../models/AgeCategory';
import { AGE_CATEGORY_SEARCH_PARAM } from './useAgeCategorySearchParam';
import { SORT_BY_QUERY_PARAM } from '../hooks/usePaginationAndSortParams';
import { updateFilterParam } from '../filter/useProductFilters';
import { ProductFilter } from '../../store/api';
import { SCOPE_CATALOGUE_OBJECT } from '../search/utils/constants';

export interface TopHitLocationDescriptorParams {
    filter: ProductFilter;
    scope: string | null | undefined;
    topHitLabel?: string;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface GenrePageLocationDescriptorParams {
    loanFormat: string | null | undefined;
    genreId: string;
    subGenreId?: string | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface CampaignDescriptorParams {
    loanFormat: string | null | undefined;
    filter: ProductFilter;
    sortBy?: string | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface LanguageLearningDescriptorParams {
    language: string | undefined | null;
    loanFormat: string | undefined | null;
    filter: ProductFilter;
    sortBy?: string | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface LanguageLearningLinkParams {
    language: string | undefined | null;
    loanFormat: string | undefined | null;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

interface LanguageLearningAllProductsParams {
    languageId: string | undefined;
    loanFormat: string;
    isMultiLanguageShopEnabled: boolean;
    shopLanguage: string | undefined;
    ageCategory: string | undefined;
}

interface LanguageLearningGenreDescriptorStringParams {
    targetLanguage: string | number | boolean;
    loanFormat: string | undefined | null;
    isMultiLanguageShopEnabled: boolean;
    shopLanguage: string | undefined;
    ageCategory: string | undefined;
}

interface ForeignLanguageDescriptorParams {
    language: string | undefined | null;
    loanFormat: string | undefined | null;
    filter: ProductFilter;
    sortBy?: string | null;
    ageCategory: string | undefined;
}

export default class RoutingUtils {
    static buildUrl(paths) {
        //TODO check if ...paths is needed
        if (typeof paths[0] === 'object') {
            paths = paths[0];
        }
        return `/${compact(flatten(paths)).join('/')}`;
    }

    static searchLocationDescriptor(searchTerm: string, searchScope: string): To {
        return { search: new URLSearchParams({ q: searchTerm, scope: searchScope }).toString() };
    }

    static topHitLocationDescriptor({
        filter,
        scope,
        topHitLabel = '',
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: TopHitLocationDescriptorParams): To {
        let searchParams = new URLSearchParams({ scope: scope ? scope : SCOPE_CATALOGUE_OBJECT.key });

        searchParams = updateFilterParam(searchParams, filter);

        if (topHitLabel) {
            searchParams.set('searchLabel', topHitLabel);
        }

        if (isMultiLanguageShopEnabled) {
            searchParams.set('shopLanguage', shopLanguage);
        }

        if (ageCategory) {
            searchParams.set('ageCategory', ageCategory);
        }

        return { pathname: '/search', search: searchParams.toString() };
    }

    static catalogueLocationDescriptor({
        loanFormat,
        filter,
        isMultiLanguageShopEnabled,
        shopLanguage = 'all',
        ageCategory,
        sortBy
    }: CatalogueLocationDescriptorParams): To {
        let searchParams = new URLSearchParams();
        if (ageCategory) {
            searchParams.set(AGE_CATEGORY_SEARCH_PARAM, ageCategory);
        }
        if (sortBy) {
            searchParams.set(SORT_BY_QUERY_PARAM, sortBy);
        }

        searchParams = updateFilterParam(searchParams, filter);

        return {
            // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
            //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
            pathname: isMultiLanguageShopEnabled
                ? `/shop/${shopLanguage}/${loanFormat ? toLower(loanFormat) : 'all'}/products`
                : `/catalogue/${loanFormat ? toLower(loanFormat) : 'all'}/products`,
            search: searchParams.toString()
        };
    }

    static campaignsLocationDescriptor({
        loanFormat,
        filter,
        isMultiLanguageShopEnabled,
        shopLanguage = 'all',
        ageCategory,
        sortBy
    }: CampaignDescriptorParams): To {
        let searchParams = new URLSearchParams();
        if (ageCategory) {
            searchParams.set(AGE_CATEGORY_SEARCH_PARAM, ageCategory);
        }
        if (sortBy) {
            searchParams.set(SORT_BY_QUERY_PARAM, sortBy);
        }

        searchParams = updateFilterParam(searchParams, filter);

        return {
            // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
            //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
            pathname: this.buildUrl([
                isMultiLanguageShopEnabled ? `shop/${shopLanguage}/unlimited` : 'unlimited',
                'campaigns',
                loanFormat ? toLower(loanFormat) : 'all'
            ]),
            search: searchParams.toString()
        };
    }

    static libxLocationDescriptor({
        filter,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory,
        sortBy
    }: {
        filter: ProductFilter;
        sortBy: string | null;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
        ageCategory: string | undefined;
    }): To {
        let searchParams = new URLSearchParams();
        if (ageCategory) {
            searchParams.set(AGE_CATEGORY_SEARCH_PARAM, ageCategory);
        }
        if (sortBy) {
            searchParams.set(SORT_BY_QUERY_PARAM, sortBy);
        }

        searchParams = updateFilterParam(searchParams, filter);

        return {
            // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
            //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
            pathname: isMultiLanguageShopEnabled
                ? `/shop/${shopLanguage}/${URL_LIBX_ROUTE}/products`
                : `/${URL_LIBX_ROUTE}/products`,
            search: searchParams.toString()
        };
    }

    static catalogueLocationDescriptorString({
        loanFormat,
        filter,
        sortBy,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: CatalogueLocationDescriptorParams): To {
        return this.catalogueLocationDescriptor({
            loanFormat,
            filter,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory,
            sortBy
        });
    }

    static campaignsLocationDescriptorString({
        loanFormat,
        filter,
        sortBy = null,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: CampaignDescriptorParams): To {
        return this.campaignsLocationDescriptor({
            loanFormat,
            filter,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory,
            sortBy
        });
    }

    static libxLocationDescriptorString({
        filter,
        sortBy = null,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        filter: ProductFilter;
        sortBy?: string | null;
        ageCategory: string | undefined;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
    }): To {
        return this.libxLocationDescriptor({
            filter,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory,
            sortBy
        });
    }

    static genrePageLocationDescriptor({
        loanFormat,
        genreId,
        subGenreId,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: GenrePageLocationDescriptorParams): To {
        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : 'catalogue';
        return {
            pathname: this.buildUrl([
                path,
                // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
                //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
                toLower(loanFormat ?? 'all'),
                'genres',
                encodeURIComponent(genreId),
                subGenreId ? encodeURIComponent(subGenreId.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static genrePageForeignLanguageLocationDescriptor({
        loanFormat,
        language,
        genreId,
        ageCategory,
        subGenreId
    }: Pick<GenrePageLocationDescriptorParams, 'genreId' | 'subGenreId' | 'ageCategory' | 'loanFormat'> & {
        language: string;
    }): Partial<Path> {
        return {
            pathname: this.buildUrl([
                'language/lote',
                encodeURIComponent(language),
                'genres',
                toLower(loanFormat ?? 'all'),
                encodeURIComponent(genreId),
                subGenreId ? encodeURIComponent(subGenreId.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static publisherPageLocationDescriptor({
        loanFormat,
        publisher,
        imprint,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        loanFormat?: string | null;
        publisher?: string | null;
        imprint?: string | null;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
        ageCategory: string | undefined;
    }): Partial<Path> {
        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : 'catalogue';

        return {
            pathname: this.buildUrl([
                path,
                // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
                //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
                toLower(loanFormat ?? 'all'),
                'publishers',
                publisher ? encodeURIComponent(publisher) : undefined,
                imprint ? encodeURIComponent(imprint.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static languagesPageLocationDescriptor({
        loanFormat,
        language,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        loanFormat?: string | null;
        language?: string | null;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
        ageCategory: string | undefined;
    }): Partial<Path> {
        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : 'catalogue';

        return {
            pathname: this.buildUrl([
                path,
                // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
                //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
                toLower(loanFormat ?? 'all'),
                'languages',
                language ? encodeURIComponent(language) : undefined,
                'products'
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static libxGenrePageLocationDescriptor({
        genreId,
        subGenreId,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        genreId: string;
        subGenreId?: string | null;
        shopLanguage: string | undefined;
        isMultiLanguageShopEnabled: boolean;
        ageCategory: string | undefined;
    }): To {
        const libxPath = isMultiLanguageShopEnabled ? `shop/${shopLanguage}/${URL_LIBX_ROUTE}` : URL_LIBX_ROUTE;

        return {
            pathname: this.buildUrl([
                libxPath,
                URL_SUFFIX_LIBX_GENRES,
                encodeURIComponent(genreId),
                subGenreId ? encodeURIComponent(subGenreId.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static libXLocalRegionPageLocationDescriptor({
        level1,
        level2,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled,
        ageCategory
    }: {
        level1: string;
        level2?: string | null;
        shopLanguage?: string | undefined;
        isMultiLanguageShopEnabled?: boolean;
        ageCategory: string | undefined;
    }): To {
        const libxLocalRegionPath = isMultiLanguageShopEnabled
            ? `shop/${shopLanguage}/${URL_LIBX_ROUTE}`
            : URL_LIBX_ROUTE;
        return {
            pathname: this.buildUrl([
                libxLocalRegionPath,
                URL_SUFFIX_LIBX_LOCAL_REGIONS,
                encodeURIComponent(level1),
                level2 ? encodeURIComponent(level2.split('->')[1]) : null
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static languageLearningDescriptor({
        language,
        loanFormat,
        filter,
        isMultiLanguageShopEnabled,
        shopLanguage,
        ageCategory,
        sortBy
    }: LanguageLearningDescriptorParams): To {
        let searchParams = new URLSearchParams();
        if (ageCategory) {
            searchParams.set(AGE_CATEGORY_SEARCH_PARAM, ageCategory);
        }
        if (sortBy) {
            searchParams.set(SORT_BY_QUERY_PARAM, sortBy);
        }

        searchParams = updateFilterParam(searchParams, filter);

        return {
            // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
            //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
            pathname: this.languageLearningLanguageLink({
                language,
                loanFormat,
                shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory
            }),
            search: searchParams.toString()
        };
    }

    static languageLearningGenreDescriptorString({
        targetLanguage,
        loanFormat,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled
    }: LanguageLearningGenreDescriptorStringParams) {
        const languageLearningPath = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : URL_FOREIGN_LANGUAGE_ROUTE;
        return `/${languageLearningPath}/${URL_LANGUAGE_LEARNING_ROUTE}/${URL_FEATURED_ROUTE}/${encodeURIComponent(
            targetLanguage
        )}/${encodeURIComponent(toLower(loanFormat ?? ''))}`;
    }

    static languageLearningDescriptorString({
        language,
        loanFormat,
        filter,
        sortBy = null,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: LanguageLearningDescriptorParams): To {
        return this.languageLearningDescriptor({
            language,
            loanFormat,
            filter,
            isMultiLanguageShopEnabled,
            shopLanguage,
            ageCategory,
            sortBy
        });
    }

    static foreignLanguageDescriptor(
        language: string | undefined | null,
        loanFormat: string | undefined | null,
        filter: ProductFilter,
        ageCategory: string | undefined,
        sortBy: string | null
    ): To {
        let searchParams = new URLSearchParams();
        if (ageCategory) {
            searchParams.set(AGE_CATEGORY_SEARCH_PARAM, ageCategory);
        }
        if (sortBy) {
            searchParams.set(SORT_BY_QUERY_PARAM, sortBy);
        }

        searchParams = updateFilterParam(searchParams, filter);

        return {
            // TODO: 'all' is not valid at this position within the path (only loan formats like 'ebooks' are
            //  allowed), but the parameter needs to be optional/nullable, so we have to provide a fallback
            pathname: this.loteLanguageLink({ language, loanFormat }),
            search: searchParams.toString()
        };
    }

    static foreignLanguageDescriptorString({
        language,
        loanFormat,
        filter,
        sortBy = null,
        ageCategory
    }: ForeignLanguageDescriptorParams): To {
        return this.foreignLanguageDescriptor(language, loanFormat, filter, ageCategory, sortBy);
    }

    static languageLearningLanguageLink({
        language,
        loanFormat,
        shopLanguage = 'all',
        isMultiLanguageShopEnabled
    }: LanguageLearningLinkParams): string | undefined {
        if (!language) {
            return undefined;
        }

        const path = isMultiLanguageShopEnabled ? `shop/${shopLanguage}` : URL_FOREIGN_LANGUAGE_ROUTE;

        return this.buildUrl([
            path,
            URL_LANGUAGE_LEARNING_ROUTE,
            encodeURIComponent(language),
            URL_ALLPRODUCTS_ROUTE,
            loanFormat ? toLower(loanFormat) : 'all'
        ]);
    }

    static loteLanguageLink({
        language,
        loanFormat
    }: {
        language: string | undefined | null;
        loanFormat: string | undefined | null;
    }): string | undefined {
        if (!language) {
            return undefined;
        }

        return this.buildUrl([
            URL_FOREIGN_LANGUAGE_ROUTE,
            URL_LOTE_ROUTE,
            encodeURIComponent(language),
            URL_ALLPRODUCTS_ROUTE,
            loanFormat ? toLower(loanFormat) : 'all'
        ]);
    }

    static foreignLanguageAllProductsPageLocationDescriptor({
        loanFormat,
        languageId,
        ageCategory
    }: {
        languageId: string | null | undefined;
        loanFormat: string;
        ageCategory: string | undefined;
    }): To {
        return {
            pathname: this.buildUrl([
                URL_FOREIGN_LANGUAGE_ROUTE,
                URL_LOTE_ROUTE,
                encodeURIComponent(languageId ?? ALL_LANGUAGES),
                URL_ALLPRODUCTS_ROUTE,
                loanFormat
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static languageLearningAllProductsPageLocationDescriptor({
        isMultiLanguageShopEnabled,
        shopLanguage,
        languageId,
        ageCategory,
        loanFormat
    }: LanguageLearningAllProductsParams): To {
        if (isMultiLanguageShopEnabled) {
            return {
                pathname: this.buildUrl([
                    'shop',
                    shopLanguage,
                    URL_LANGUAGE_LEARNING_ROUTE,
                    encodeURIComponent(languageId ?? ALL_LANGUAGES),
                    URL_ALLPRODUCTS_ROUTE,
                    loanFormat
                ]),
                search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
            };
        }

        return {
            pathname: this.buildUrl([
                URL_FOREIGN_LANGUAGE_ROUTE,
                URL_LANGUAGE_LEARNING_ROUTE,
                encodeURIComponent(languageId ?? ALL_LANGUAGES),
                URL_ALLPRODUCTS_ROUTE,
                loanFormat
            ]),
            search: !ageCategory ? undefined : `ageCategory=${ageCategory}`
        };
    }

    static getForeignLanguageUrlWithReplacedLanguage(
        language: string,
        pathname: string,
        isMultiLanguageShopEnabled: boolean,
        ageCategory: AgeCategory | undefined
    ): To | null {
        const searchParams = new URLSearchParams();
        if (ageCategory) {
            searchParams.set('ageCategory', ageCategory);
        }

        if (isMultiLanguageShopEnabled) {
            const match = matchPath('/shop/:shopLanguage/languagelearning/:languageId/:page/:loanFormat/*', pathname);

            if (!match) {
                return null;
            }

            const { shopLanguage, page, loanFormat } = match.params;

            return {
                pathname: `/shop/${shopLanguage}/languagelearning/${encodeURIComponent(
                    language
                )}/${page}/${loanFormat}`,
                search: searchParams.toString()
            };
        }

        const match = matchPath('/language/:section/:languageId/:page/:loanFormat/*', pathname);

        if (!match) {
            return null;
        }

        const { section, page, loanFormat } = match.params;

        return {
            pathname: `/language/${section}/${encodeURIComponent(language)}/${page}/${loanFormat}`,
            search: searchParams.toString()
        };
    }
}
